/* PasswordInput — password field with show/hide toggle */

import * as React from 'react'
import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Input, type InputProps } from '@/components/ui/Input'

/**
 * Derived from `InputProps`, not from raw HTML attributes. It composes `Input`, so re-declaring
 * the prop type meant the wrapper could not express what the thing it wraps supports — the
 * moment `Input` gained a `size` variant, this stopped compiling. A wrapper's type is the
 * wrapped component's type.
 */
export type PasswordInputProps = Omit<InputProps, 'type'>

/**
 * Composes Input rather than re-declaring it (W4.T5.1).
 *
 * This previously rendered its own <input> whose class string was Input's copied
 * character-for-character, minus the `file:` line and plus `pr-10`. Nine of ten class lines were
 * duplicated, so every change to Input's height, focus ring, radius or invalid state silently
 * skipped the password field — the one input where looking different reads as "this is a
 * different kind of field".
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, ...props }, ref) => {
    const [visible, setVisible] = useState(false)

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={cn('pr-10', className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          tabIndex={-1}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-subtle hover:text-text transition-colors disabled:pointer-events-none"
        >
          {visible ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    )
  },
)
PasswordInput.displayName = 'PasswordInput'
